import React from 'react';
import { connect } from 'react-redux';
import { Flex, Headline, ButtonBox, Card, Title, Tooltip, Text, Block } from '@upsales/components';
import EditorHeaderButton from 'Components/EditorHeader/EditorHeaderButton';
import Stages from '../Stages';
import T from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import { setBudget } from 'Store/reducers/SalesCoachReducer';
import { cloneDeep } from 'lodash';
import { Fade } from '@upsales/components/animations';
import { salesCoachTracker } from 'App/babel/helpers/Tracker';
import { getStages } from 'Store/actions/AppGetterActions';
import OrderStage from 'App/resources/Model/OrderStage';

import './VerifyBudget.scss';
import SalesProcessAddonHint from 'Components/Admin/SalesProcess/SalesCoach/SalesProcessAddonHint';
import { asyncModalAdapter, setupComponentCompatibility } from 'App/helpers/angularPortingHelpers';

type StageObj = {
	active: boolean;
	required?: boolean;
};

type Selected = {
	[key: number]: StageObj;
};

type Budget = {
	active: boolean;
	entireSalesProcess: boolean;
	stages: Selected;
};

type Props = {
	reject: () => void;
	budget: Budget;
	setBudget: (budget: Object) => void;
	getStages: (
		type: 'all' | 'order' | 'opportunity' | 'lost' | 'won' | 'excludedIds',
		skipAuth: boolean
	) => OrderStage[] | number[];
};

type State = {
	budget: Budget;
};

const mapStateToProps = ({ SalesCoachReducer }: { SalesCoachReducer: { budget: Budget } }) => ({
	budget: SalesCoachReducer.budget
});

const mapDispatchToProps = { setBudget, getStages };

class VerifyBudget extends React.Component<Props, State> {
	lang: { [key: string]: string };

	constructor(p: Props) {
		super(p);
		this.lang = {
			cancel: T('default.cancel'),
			save: T('default.save'),
			descriptionBudget: T('admin.newSalesProcess.descriptionBudget'),
			atWhatPoint: T('admin.newSalesProcess.atWhatPoint'),
			specificStage: T('admin.newSalesProcess.specificStage'),
			entireSalesProcess: T('admin.newSalesProcess.entireSalesProcess'),
			verifyingBudget: T('admin.newSalesProcess.verifyingBudget'),
			backToChecklist: T('admin.newSalesProcess.backToChecklist'),
			chooseStagesToSave: T('admin.newSalesProcess.chooseStagesToSave')
		};
		this.state = { budget: cloneDeep(p.budget) };
	}

	track = (id: number, active: boolean) => {
		const { getStages } = this.props;
		const probability = (getStages('opportunity', false) as OrderStage[]).find(
			stage => stage.id === id
		)?.probability;
		if (!probability) {
			return;
		}

		const { ACTIVATED_BUDGET, DEACTIVATED_BUDGET } = salesCoachTracker.events;
		const event = active ? ACTIVATED_BUDGET : DEACTIVATED_BUDGET;
		salesCoachTracker.track(event, { probability });
	};

	flipSelected = (id: number) => {
		const { budget } = this.state;
		if (!budget.stages[id]) {
			budget.stages[id] = { active: false };
		}
		budget.stages[id].active = !budget.stages[id].active;
		if (!budget.stages[id].active && budget.stages[id]?.required) {
			budget.stages[id].required = false;
		}

		this.track(id, budget.stages[id].active);
		this.setState({ budget });
	};

	flipRequired = (id: number) => {
		const { budget } = this.state;
		if (!budget.stages[id]) {
			budget.stages[id] = { active: false };
		}
		budget.stages[id].required = !budget.stages[id]?.required;
		if (budget.stages[id].required && !budget.stages[id]?.active) {
			budget.stages[id].active = true;
		}
		this.setState({ budget });
	};

	toggleEntireSalesProcess = () => {
		const { budget } = this.state;
		if (budget.entireSalesProcess) {
			return;
		}

		this.setState({ budget: { ...budget, entireSalesProcess: true, stages: {} } });
	};

	toggleSpecificStage = () => {
		const { budget } = this.state;
		if (!budget.entireSalesProcess) {
			return;
		}

		this.setState({ budget: { ...budget, entireSalesProcess: false, stages: {} } });
	};

	haveNoStagesSelected() {
		if (!this.state.budget.entireSalesProcess) {
			const activeStage = Object.values(this.state.budget.stages).find(stage => {
				return stage.active;
			});
			return !activeStage;
		}
		return false;
	}

	render() {
		const { setBudget, reject } = this.props;
		const { budget } = this.state;

		const classNames = new BemClass('VerifyBudget');

		return (
			<Fade>
				<Flex className={classNames.b()} justifyContent="center" direction="column" gap="u7">
					<Card>
						<Flex justifyContent="space-between" flex={[0, 0, '50px']} alignItems="center" space="pll">
							<Title>{this.lang.verifyingBudget}</Title>
							<div>
								<EditorHeaderButton
									title={this.lang.cancel}
									onClick={() => {
										reject();
									}}
									supertitle={undefined}
									className={undefined}
									noIcon
									next={false}
								/>
								<Tooltip title={this.lang.chooseStagesToSave} disabled={!this.haveNoStagesSelected()}>
									<EditorHeaderButton
										title={this.lang.save}
										supertitle={this.lang.backToChecklist}
										disabled={this.haveNoStagesSelected()}
										onClick={() => {
											setBudget({ ...budget, active: true });
											reject();
										}}
										next
										className="VerifyBudget__EditorHeaderButton"
										noIcon={false}
									/>
								</Tooltip>
							</div>
						</Flex>
					</Card>
					<div className={classNames.elem('content').b()}>
						<Headline>{this.lang.verifyingBudget}</Headline>
						<Text color="grey-11">{this.lang.descriptionBudget}</Text>
						<Block space="mtxl">
							<Title size="lg">{this.lang.atWhatPoint}</Title>
						</Block>
						<Flex className={classNames.elem('buttonBoxes').b()} space="mtl" gap="u4">
							<ButtonBox
								onClick={this.toggleEntireSalesProcess}
								selected={budget.entireSalesProcess}
								title={this.lang.entireSalesProcess}
							/>
							<ButtonBox
								onClick={this.toggleSpecificStage}
								selected={!budget.entireSalesProcess}
								title={this.lang.specificStage}
							/>
						</Flex>
						<Fade visible={!budget.entireSalesProcess}>
							<Block space="mtl">
								<Stages selected={budget.stages} onToggleItem={this.flipSelected} />
								<SalesProcessAddonHint />
							</Block>
						</Fade>
					</div>
				</Flex>
			</Fade>
		);
	}
}

export const detached = VerifyBudget;

const Component = connect(mapStateToProps, mapDispatchToProps)(VerifyBudget);

export const VerifyBudgetModal = setupComponentCompatibility(Component, {
	modalName: 'FullscreenModal'
});

export const openVerifyBudgetModal = asyncModalAdapter({
	featureFlag: 'VERIFY_BUDGET_REACT',
	openModalName: 'VerifyBudgetModal',
	upModalName: 'VerifyBudget'
});

export default Component;
